'use client';

import {
  HeadphonesIcon,
  Ticket,
  ShoppingCart,
  Lightbulb,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Clock,
  User,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { createTile, type BaseTileRenderProps } from './BaseTile';
import type { SupportResult } from '../types';

function PriorityBadge({ priority }: { priority?: SupportResult['metadata']['priority'] }) {
  if (!priority) return null;

  const priorityConfig = {
    low: {
      icon: Clock,
      label: 'Low',
      className: 'bg-slate-500/10 text-slate-400 border-slate-500/30',
    },
    medium: {
      icon: AlertCircle,
      label: 'Medium',
      className: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    },
    high: {
      icon: AlertTriangle,
      label: 'High',
      className: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    },
    urgent: {
      icon: AlertTriangle,
      label: 'Urgent',
      className: 'bg-red-500/10 text-red-400 border-red-500/30',
    },
  };

  const config = priorityConfig[priority];
  if (!config) return null;

  const Icon = config.icon;

  return (
    <Badge
      variant="outline"
      className={cn('text-xs gap-1', config.className)}
    >
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
}

function StatusBadge({ status }: { status?: string }) {
  if (!status) return null;

  const statusLower = status.toLowerCase();
  let className = 'bg-slate-500/10 text-slate-400 border-slate-500/30';
  let Icon = Clock;

  if (statusLower.includes('open') || statusLower.includes('pending')) {
    className = 'bg-amber-500/10 text-amber-400 border-amber-500/30';
    Icon = Clock;
  } else if (statusLower.includes('resolved') || statusLower.includes('closed') || statusLower.includes('complete')) {
    className = 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30';
    Icon = CheckCircle;
  } else if (statusLower.includes('escalat')) {
    className = 'bg-red-500/10 text-red-400 border-red-500/30';
    Icon = AlertTriangle;
  }

  return (
    <Badge
      variant="outline"
      className={cn('text-xs gap-1', className)}
    >
      <Icon className="w-3 h-3" />
      {status}
    </Badge>
  );
}

function getSupportIcon(result: SupportResult) {
  const { metadata } = result;

  if (metadata.ticketId) return Ticket;
  if (metadata.orderId) return ShoppingCart;
  if (result.source === 'support_data' && result.title.toLowerCase().includes('lesson')) {
    return Lightbulb;
  }

  return HeadphonesIcon;
}

export const SupportTile = createTile<SupportResult>(
  'SupportTile',
  (result) => {
    const { metadata } = result;
    const Icon = getSupportIcon(result);

    const idParts: string[] = [];
    if (metadata.ticketId) idParts.push(`Ticket: ${metadata.ticketId}`);
    if (metadata.orderId) idParts.push(`Order: ${metadata.orderId}`);
    const subtitle = idParts.join(' | ') || undefined;

    const renderProps: BaseTileRenderProps = {
      icon: Icon,
      iconColor: 'text-amber-400',
      title: result.title,
      subtitle,
      preview: result.preview || metadata.resolution,
      metadata: [],
      tags: [],
      rightContent: (
        <div className="flex items-center gap-2">
          <StatusBadge status={metadata.status} />
          <PriorityBadge priority={metadata.priority} />
        </div>
      ),
    };

    if (metadata.assignedTo) {
      renderProps.metadata!.push({
        label: 'Assigned',
        value: metadata.assignedTo,
        icon: User,
      });
    }

    if (metadata.resolution && metadata.resolution !== result.preview) {
      renderProps.bottomContent = (
        <div className="text-sm text-slate-400 bg-slate-800/30 rounded-md p-2 border border-slate-700/50">
          <span className="text-slate-500 text-xs uppercase tracking-wider">Resolution:</span>
          <p className="mt-1">{metadata.resolution}</p>
        </div>
      );
    }

    return renderProps;
  }
);

export default SupportTile;
